/**
 * Formato do relatório "Afiliados" exportado pela BuyGoods (Reports → Affiliates → Export),
 * o mesmo que chega por e-mail e que o gestor consolida na planilha.
 *
 *   linha 1: título com o período  — "Afiliados Abril April 01, 2026 - April 30, 2026"
 *   linha 3: "Report Date:" | "10-09-2026"
 *   linha 5: cabeçalho (21 colunas, abaixo)
 *   linhas seguintes: uma por (Account, Affiliate) — o mesmo par pode repetir (contas
 *                     diferentes na BuyGoods com o mesmo nome); todas somam
 *   última linha: "Total"
 */
/** Campos numéricos de cada linha, na ordem das colunas do relatório (após Account e Affiliate). */
export const REPORT_METRIC_FIELDS = [
    'visitors',
    'checkoutPage',
    'customers',
    'orders',
    'conversions',
    'conversionRate',
    'grossSales',
    'refundRate',
    'chargebackRate',
    'affiliateEpc',
    'affiliateEpo',
    'commissionsNet',
    'commissionsCountNet',
    'totalCbsRefunds',
    'taxes',
    'aov',
    'epc',
    'epo',
    'netSales',
];
export const REPORT_COLUMNS = [
    { field: 'visitors', header: 'Visitors', label: 'Visitantes', kind: 'int' },
    { field: 'checkoutPage', header: 'Checkout Page', label: 'Checkout (%)', kind: 'rate' },
    { field: 'customers', header: 'Customers', label: 'Clientes', kind: 'int' },
    { field: 'orders', header: 'Orders (FE+BE)', label: 'Pedidos (FE+BE)', kind: 'int' },
    { field: 'conversions', header: 'Conversions', label: 'Conversões', kind: 'int' },
    { field: 'conversionRate', header: 'Conversion Rate', label: 'Conversão (%)', kind: 'rate' },
    { field: 'grossSales', header: 'Gross Sales', label: 'Faturamento bruto', kind: 'money' },
    { field: 'refundRate', header: 'Refund Rate', label: 'Reembolso (%)', kind: 'rate' },
    { field: 'chargebackRate', header: 'Chargeback Rate', label: 'Chargeback (%)', kind: 'rate' },
    { field: 'affiliateEpc', header: 'Affiliate EPC', label: 'EPC do afiliado', kind: 'money' },
    { field: 'affiliateEpo', header: 'Affiliate EPO', label: 'EPO do afiliado', kind: 'money' },
    {
        field: 'commissionsNet',
        header: 'Commissions Net',
        label: 'Comissões líquidas',
        kind: 'money',
    },
    {
        field: 'commissionsCountNet',
        header: 'Commissions Count Net',
        label: 'Qtd. comissões',
        kind: 'int',
    },
    {
        field: 'totalCbsRefunds',
        header: 'Total CBs & Refunds',
        label: 'Chargebacks e reembolsos',
        kind: 'money',
    },
    { field: 'taxes', header: 'Taxes', label: 'Impostos', kind: 'money' },
    { field: 'aov', header: 'AOV', label: 'Ticket médio', kind: 'money' },
    { field: 'epc', header: 'EPC', label: 'EPC', kind: 'money' },
    { field: 'epo', header: 'EPO', label: 'EPO', kind: 'money' },
    { field: 'netSales', header: 'Net Sales', label: 'Faturamento líquido', kind: 'money' },
];
/** Cabeçalhos das duas primeiras colunas, que identificam a linha. */
export const REPORT_ACCOUNT_HEADER = 'Account';
export const REPORT_AFFILIATE_HEADER = 'Affiliate';
const MONTHS = {
    january: 1,
    february: 2,
    march: 3,
    april: 4,
    may: 5,
    june: 6,
    july: 7,
    august: 8,
    september: 9,
    october: 10,
    november: 11,
    december: 12,
    jan: 1,
    feb: 2,
    mar: 3,
    apr: 4,
    jun: 6,
    jul: 7,
    aug: 8,
    sep: 9,
    sept: 9,
    oct: 10,
    nov: 11,
    dec: 12,
};
const pad = (n) => String(n).padStart(2, '0');
export const isoDate = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;
/**
 * Extrai o período do título do relatório ("… April 01, 2026 - April 30, 2026") ou de um texto
 * com duas datas ISO ("2026-10-01_2026-10-05", como no nome do arquivo exportado).
 */
export function parseReportPeriod(text) {
    if (!text)
        return null;
    const en = /([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})\s*[-–—]\s*([A-Za-z]{3,9})\.?\s+(\d{1,2}),?\s+(\d{4})/.exec(text);
    if (en) {
        const m1 = MONTHS[en[1].toLowerCase()];
        const m2 = MONTHS[en[4].toLowerCase()];
        if (m1 && m2) {
            return {
                start: isoDate(Number(en[3]), m1, Number(en[2])),
                end: isoDate(Number(en[6]), m2, Number(en[5])),
            };
        }
    }
    const iso = /(\d{4}-\d{2}-\d{2})\D{1,5}(\d{4}-\d{2}-\d{2})/.exec(text);
    if (iso)
        return { start: iso[1], end: iso[2] };
    return null;
}
/** "10-09-2026" (MM-DD-YYYY, como a BuyGoods grava em "Report Date") → YYYY-MM-DD. */
export function parseReportDate(text) {
    if (!text)
        return null;
    const us = /(\d{1,2})-(\d{1,2})-(\d{4})/.exec(text);
    if (us)
        return isoDate(Number(us[3]), Number(us[1]), Number(us[2]));
    const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(text);
    if (iso)
        return `${iso[1]}-${iso[2]}-${iso[3]}`;
    return null;
}
/** "2026-04" a partir de uma data ISO. */
export function monthKeyOf(isoDay) {
    return isoDay.slice(0, 7);
}
/** Um período precisa caber num único mês: é assim que ele entra no faturamento mensal. */
export function periodWithinOneMonth(p) {
    return monthKeyOf(p.start) === monthKeyOf(p.end);
}
export function isValidIsoDate(s) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s))
        return false;
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}
export const MONTH_LABELS_PT = [
    'jan',
    'fev',
    'mar',
    'abr',
    'mai',
    'jun',
    'jul',
    'ago',
    'set',
    'out',
    'nov',
    'dez',
];
/** "2026-04" → "abr/26" */
export function monthLabel(monthKey) {
    const [y, m] = monthKey.split('-');
    const idx = Number(m) - 1;
    return `${MONTH_LABELS_PT[idx] ?? m}/${(y ?? '').slice(2)}`;
}
/** "2026-04-01".."2026-04-05" → "01–05/04"; um dia só → "01/04". */
export function periodLabel(p) {
    const [, m1, d1] = p.start.split('-');
    const [, m2, d2] = p.end.split('-');
    if (p.start === p.end)
        return `${d1}/${m1}`;
    if (m1 === m2)
        return `${d1}–${d2}/${m1}`;
    return `${d1}/${m1}–${d2}/${m2}`;
}
